import { VlocStartNewDisbursement } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, router, Stack, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import LendingCard from 'shared/components/vxl/cards/LendingCard/LendingCard';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import theme from 'shared/styles/theme';

const gotToIncreaseYourLimits = () => {
  router.navigate('/varo-line-of-credit/increase-your-limits');
};

type TakeOutScreenProps = {
  data?: VlocStartNewDisbursement | TakeOutData;
};

type LendingTier = {
  id: string;
  tierAmount?: {
    amount?: string;
    formattedWithPlusPrefix?: string;
  } | null;
  term?: string | null;
  monthlyPayment?: string | null;
  fee?: {
    amount?: string;
    formattedWithPlusPrefix?: string;
  } | null;
};

type TakeOutData = {
  accountId?: string;
  headerTitle?: string | null;
  vlocTiers?: LendingTier[];
};

const TakeOutScreen = ({ data }: TakeOutScreenProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('vloc');
  const { bottom } = useSafeAreaInsets();
  const vlocTierData = data?.vlocTiers ?? null;

  useViewDidFocusScreenAnalyticsEvent({
    key: '/varo-line-of-credit/take-out',
  });

  const formattedCardData = vlocTierData?.map((tier) => ({
    id: tier.id,
    text: String(tier.tierAmount?.formattedWithPlusPrefix ?? '').split('.')[0],
    details: [
      {
        id: 1,
        titleText: translatedText('triesData.term'),
        subtitleText: tier.term,
      },
      {
        id: 2,
        titleText: translatedText('triesData.monthlyPayment'),
        subtitleText: tier.mothlyPayment,
      },
      {
        id: 3,
        titleText: translatedText('triesData.fee'),
        subtitleText: String(tier.fee?.formattedWithPlusPrefix ?? '').split(
          '.',
        )[0],
      },
    ],
    analytics: {
      amount: tier?.tierAmount?.amount ?? '',
      source_name: 'vloc_tier_selection_page',
      term: tier?.term,
      fee: tier?.fee?.amount ?? '',
      subcategory: 'taking_out_vloc',
    },
  }));

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <ScrollView
        style={styles.scrollView}
        bounces={false}
        alwaysBounceVertical={false}
      >
        <Stack.Screen
          options={{
            title: translatedText('vlocGeneral.lineOfCreditTitle'),
            headerBackVisible: true,
            headerShown: true,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar
                trailingAccessories={
                  <IconButton
                    iconKey="SETTINGS"
                    iconButtonAccessibilityLabel="SETTINGS"
                    iconOnPress={() => {
                      router.navigate({
                        pathname: '/varo-line-of-credit/manage-account',
                        params: {
                          accountId: data?.accountId ?? '',
                        },
                      });
                    }}
                    analyticsId="takeout_manageAccountClicked"
                  />
                }
                {...props}
              />
            ),
          }}
        />
        {data?.headerTitle ? (
          <TitleLgText style={styles.title} numberOfLines={0}>
            {data.headerTitle}
          </TitleLgText>
        ) : null}
        {formattedCardData?.map((card, index) => (
          <View
            key={card.id}
            style={index !== formattedCardData.length - 1 && styles.cardSpacing}
          >
            <LendingCard
              text={card.text}
              details={card.details}
              onPress={() =>
                router.push({
                  pathname: '/varo-line-of-credit/review',
                  params: {
                    tierId: card.id,
                    subcategory: 'taking_out_vloc',
                    name: 'Taking Out VLOC Flow Exited',
                  },
                })
              }
              analyticsId="takeout_vloc_tier_selected"
              customAttributes={card?.analytics}
            />
          </View>
        ))}
      </ScrollView>
      <HairLineDivider />
      <View style={styles.buttonContainer}>
        <Button
          label={translatedText('vlocTakeOut.button')}
          onPress={gotToIncreaseYourLimits}
          type="SECONDARY"
          analyticsId="borrow_more"
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    alignItems: 'center',
    padding: theme.spacing.m,
    width: '100%',
  },
  cardSpacing: {
    marginBottom: theme.spacing.m,
  },
  container: {
    alignSelf: 'center',
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingVertical: theme.spacing.m,
    width: '100%',
  },
  scrollView: {
    paddingHorizontal: theme.spacing.m,
  },
  title: {
    paddingBottom: theme.spacing.m,
  },
});

export default TakeOutScreen;
